import { cloneDeep } from 'lodash';
import { create } from 'zustand';

import { generateSelectEventType } from '@src/emp-3.0/components/custom-dimension/utils';
import { ICreateCustomDimensionPayload, IUpdateCustomDimensionPayload } from '@src/emp-3.0/types';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';

type TCustomDimensionPayload =
  | ICreateCustomDimensionPayload
  | IUpdateCustomDimensionPayload
  | { [key: string]: string | string[] | number | undefined };

interface ICustomDimensionPanelStore {
  customDimensionPanelOpen: boolean;
  selectEventType: string;
  attributeFiltersPayload?: any[];
  dimensionIdentifier?: string;
  setAttributeFiltersPayload: (filters?: any[]) => void;
  setCustomDimensionPanelOpen: (open: boolean) => void;
  setSelectEventType: (type: string) => void;
  customDimensionPayload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload;
  setCustomDimensionPayload: (payload: TCustomDimensionPayload) => void;
  resetCustomDimensionStore: () => void;
}

const defaultCustomDimensionPayload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload =
  {
    attribute_name: '',
    display_name: '',
    category: undefined,
    mapped_event_names: [],
    attribute_type: undefined,
    is_promoted_as_global: 0,
  };

// attribute name changed
function isDimensionNameChanged(dimension: TCustomDimensionPayload): boolean {
  return (
    dimension.display_name === undefined &&
    dimension.category === undefined &&
    dimension.attribute_type === undefined &&
    dimension.mapped_event_names === undefined
  );
}

export const useCustomDimensionPanelStore = create<ICustomDimensionPanelStore>((set, get) => ({
  customDimensionPanelOpen: false,
  selectEventType: ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  attributeFiltersPayload: undefined,
  customDimensionPayload: cloneDeep(defaultCustomDimensionPayload),
  setAttributeFiltersPayload: (attributeFiltersPayload) => set(() => ({ attributeFiltersPayload })),
  setCustomDimensionPanelOpen: (customDimensionPanelOpen) =>
    set(() => ({ customDimensionPanelOpen })),
  setSelectEventType: (selectEventType) => set(() => ({ selectEventType })),
  setCustomDimensionPayload: (payload) =>
    set(() => {
      const { customDimensionPayload, selectEventType } = get();
      const { attribute_type, mapped_event_names } = customDimensionPayload;
      const attributeType = (payload.attribute_type || attribute_type) as number;
      const mappedEventNames = (
        isDimensionNameChanged(payload) ? [] : payload.mapped_event_names || mapped_event_names
      ) as string[];
      const newCustomDimensionPayload = {
        ...customDimensionPayload,
        ...payload,
        mapped_event_names: mappedEventNames,
      };
      return {
        customDimensionPayload: newCustomDimensionPayload,
        selectEventType:
          payload.mapped_event_names || payload.attribute_type
            ? generateSelectEventType(attributeType, mappedEventNames)
            : selectEventType,
      };
    }),
  resetCustomDimensionStore: () =>
    set(() => ({
      attributeFiltersPayload: undefined,
      customDimensionPayload: cloneDeep(defaultCustomDimensionPayload),
    })),
}));
